import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	upsertCartridge,
	removeCartridge,
} from "../repositories/CartridgesRepository";
import type { Cartridge } from "../models/Cartridge";

interface UseUpsertCartridgeOptions {
	onSuccess?: (data: Cartridge) => void;
	onError?: (error: Error) => void;
}

export const useUpsertCartridge = ({
	onSuccess,
	onError,
}: UseUpsertCartridgeOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: upsertCartridge,
		onSuccess: async (data) => {
			await queryClient.invalidateQueries({ queryKey: ["cartridges"] });
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertCartridge: mutation.mutate,
		isUpserting: mutation.isPending,
	};
};

interface UseRemoveCartridgeOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
}

export const useRemoveCartridge = ({
	onSuccess,
	onError,
}: UseRemoveCartridgeOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: removeCartridge,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["cartridges"] });
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		removeCartridge: mutation.mutate,
		isRemoving: mutation.isPending,
	};
};
